import { config } from '@/config';
import { apiClient, ApiError, buildCookieAuthToken, type JsonValue } from '@/shared/data/client';
import { getErrorMessage } from '@/shared/data/errors';
import type { AuthError, AuthSession, SignInPayload, SignUpPayload } from './types';
import type { OrganisationDto } from '@flowqa/shared';

type AuthResponse = {
  userId: string;
  orgId?: string;
  orgName?: string;
  lastOrganisationId?: string | null;
  lastProjectId?: string | null;
  onboardingCompletedAt?: string | null;
  emailVerifiedAt?: string | null;
  accessToken: string;
  refreshToken?: string;
  roles?: string[];
};

type AuthBootstrapResponse = {
  userId: string;
  orgId?: string;
  orgName?: string;
  lastOrganisationId?: string | null;
  lastProjectId?: string | null;
  onboardingCompleted: boolean;
  emailVerified: boolean;
  roles?: string[];
  preferredOrganisationId: string | null;
  preferredProjectId: string | null;
};

const AUTH_ENDPOINTS = {
  signIn: '/auth/signin',
  signUp: '/auth/signup',
  accountRecoveryStatus: '/auth/account-recovery/status',
  bootstrap: '/auth/bootstrap',
  verifyEmail: '/auth/verify-email',
  refresh: '/auth/refresh',
  signOut: '/auth/signout',
  resetPasswordRequest: '/auth/reset-password/request',
  resetPasswordConfirm: '/auth/reset-password/confirm',
} as const;

type AuthErrorInfo = Error & { code?: string; details?: JsonValue };

const toAuthError = (err: unknown): AuthError => {
  if (err instanceof ApiError) {
    return { code: err.code ?? 'AUTH_ERROR', message: err.message, details: err.details };
  }
  if (err instanceof Error) {
    const code = (err as AuthErrorInfo).code;
    return { code: code ?? 'AUTH_ERROR', message: err.message };
  }
  return { code: 'AUTH_ERROR', message: getErrorMessage(err) };
};

const toSession = (payload: AuthResponse): AuthSession => ({
  userId: payload.userId,
  orgId: payload.orgId,
  orgName: payload.orgName,
  lastOrganisationId: payload.lastOrganisationId ?? null,
  lastProjectId: payload.lastProjectId ?? null,
  onboardingCompleted: Boolean(payload.onboardingCompletedAt),
  emailVerified: Boolean(payload.emailVerifiedAt),
  tokens: { accessToken: buildCookieAuthToken(payload.userId) },
  roles: payload.roles,
  expiresAt: config.sessionMaxAgeMs ? Date.now() + config.sessionMaxAgeMs : undefined,
});

const toSessionFromBootstrap = (
  payload: AuthBootstrapResponse,
  accessToken: string,
): AuthSession => ({
  userId: payload.userId,
  orgId: payload.orgId ?? payload.preferredOrganisationId ?? undefined,
  orgName: payload.orgName,
  lastOrganisationId: payload.preferredOrganisationId ?? payload.lastOrganisationId ?? null,
  lastProjectId: payload.preferredProjectId ?? payload.lastProjectId ?? null,
  onboardingCompleted: payload.onboardingCompleted,
  emailVerified: payload.emailVerified,
  tokens: { accessToken },
  roles: payload.roles,
  expiresAt: config.sessionMaxAgeMs ? Date.now() + config.sessionMaxAgeMs : undefined,
});

export const authApi = {
  signIn: async (input: SignInPayload): Promise<AuthSession> => {
    try {
      const payload = await apiClient.post<AuthResponse>(AUTH_ENDPOINTS.signIn, input, {
        retries: 1,
        skipAuthRecovery: true,
      });
      return toSession(payload);
    } catch (err) {
      const authErr = toAuthError(err);
      const error = new Error(authErr.message);
      (error as AuthErrorInfo).code = authErr.code;
      (error as AuthErrorInfo).details = authErr.details;
      throw error;
    }
  },
  signUp: async (input: SignUpPayload): Promise<AuthSession> => {
    try {
      const payload = await apiClient.post<AuthResponse>(AUTH_ENDPOINTS.signUp, input, {
        retries: 1,
        skipAuthRecovery: true,
      });
      return toSession(payload);
    } catch (err) {
      const authErr = toAuthError(err);
      const error = new Error(authErr.message);
      (error as AuthErrorInfo).code = authErr.code;
      (error as AuthErrorInfo).details = authErr.details;
      throw error;
    }
  },
  refresh: async (): Promise<AuthSession> => {
    try {
      const payload = await apiClient.post<AuthResponse>(
        AUTH_ENDPOINTS.refresh,
        {},
        {
          retries: 0,
          skipAuthRecovery: true,
        },
      );
      return toSession(payload);
    } catch (err) {
      const authErr = toAuthError(err);
      const error = new Error(authErr.message);
      (error as AuthErrorInfo).code = authErr.code;
      (error as AuthErrorInfo).details = authErr.details;
      throw error;
    }
  },
  signOut: async (): Promise<void> => {
    try {
      await apiClient.post(AUTH_ENDPOINTS.signOut, {}, { retries: 0 });
    } catch {
      // ignore sign-out failures
    }
  },
  verifyEmail: async (token: string): Promise<void> => {
    await apiClient.post<{ status: 'verified' }>(
      AUTH_ENDPOINTS.verifyEmail,
      { token },
      {
        retries: 1,
        skipAuthRecovery: true,
      },
    );
  },
  resetPasswordRequest: async (email: string): Promise<void> => {
    await apiClient.post<{ status: 'reset-requested' }>(
      AUTH_ENDPOINTS.resetPasswordRequest,
      { email },
      {
        retries: 1,
        skipAuthRecovery: true,
      },
    );
  },
  resetPasswordConfirm: async (input: { token: string; password: string }): Promise<void> => {
    await apiClient.post<{ status: 'password-reset' }>(AUTH_ENDPOINTS.resetPasswordConfirm, input, {
      retries: 1,
      skipAuthRecovery: true,
    });
  },
  getAccountRecoveryStatus: async (
    email: string,
  ): Promise<{
    status: 'none' | 'active' | 'deleted_restorable' | 'deleted_expired';
    restorable: boolean;
  }> => {
    return await apiClient.post<{
      status: 'none' | 'active' | 'deleted_restorable' | 'deleted_expired';
      restorable: boolean;
    }>(
      AUTH_ENDPOINTS.accountRecoveryStatus,
      { email },
      {
        retries: 1,
        skipAuthRecovery: true,
      },
    );
  },
  fetchOrganisation: async (orgId: string): Promise<OrganisationDto> => {
    const organisation = await apiClient.get<OrganisationDto>(`organisations/${orgId}`, {
      retries: 1,
    });
    return organisation;
  },
  bootstrapSession: async (
    accessToken: string,
    opts?: { allowProvisioning?: boolean },
  ): Promise<AuthSession> => {
    const payload = await apiClient.get<AuthBootstrapResponse>(AUTH_ENDPOINTS.bootstrap, {
      token: accessToken,
      preferProvidedToken: true,
      orgId: null,
      retries: 1,
      skipAuthRecovery: true,
      headers: opts?.allowProvisioning ? { 'x-flowqa-auth-provision': 'signup' } : undefined,
    });
    return toSessionFromBootstrap(payload, accessToken);
  },
};
